import React, { useCallback, useEffect, useState } from "react";
import ModalContainer from "../ModalContainer";
import { EmailField, NameField, PhoneField } from "../../form/Fields";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { zodContestSubmit } from "@/libs/zod/zodValidation";
import styled from "@emotion/styled";
import { CustomTextArea, TextInput } from "../../form";
import { SHORT_FORM_VIDEO_LINK_PH } from "@/constants/placeholderTexts";
import { Flex, Popconfirm } from "antd";
import { PrimaryButton } from "../../buttons";
import { color, image } from "@/theme";
import { CommonDescriptionTwo, CommonTitleFour } from "../../fonts/Fonts";
import { mq } from "@/libs/react-responsive/mediaQuery";
import { useMutation } from "@tanstack/react-query";
import { SHORT_FORM_CONTEST_PARTICIPATE_API_URL } from "@/constants/apiUrls";
import axios from "axios";
import { SUCCESS_CODE } from "@/constants/responseResults";
import { replaceAllDash } from "@/utils/Functions";
import { ContestCheckBoxGroup } from "..";

const Container = styled.form(() => ({
  display: "flex",
  maxWidth: "50rem",
  flexDirection: "column",
  gap: "1rem",
  margin: "0 auto",
}));

const PageTitle = styled(CommonTitleFour)(() => ({
  margin: "2rem 0",
  textAlign: "center",
}));

const SubmitButtonWrapper = styled.div(() => ({
  display: "flex",
  justifyContent: "center",
  marginBlock: "6rem",
}));

const SuccessImage = styled.img(() => ({
  width: "100%",
  maxWidth: "24rem",

  [mq("tablet")]: {
    maxWidth: "30rem",
  },
}));

const ContestFormModal = ({ onCancel, modalOpen }) => {
  const [isAI, setIsAI] = useState({
    yes: false,
    no: true,
  });

  const [tools, setTools] = useState([
    { name: "ChatGPT", value: false },
    { name: "Midjourney", value: false },
    { name: "Gamma", value: false },
    { name: "wrtn", value: false },
    { name: "Bing", value: false },
    { name: "Adobe ai", value: false },
    { name: "기타", value: false },
  ]);

  const [aiToolError, setAiToolError] = useState(false);

  const [open, setOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    trigger,
    setError,
    clearErrors,
    watch,
  } = useForm({
    resolver: zodResolver(zodContestSubmit),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      participant_email: "",
      participant_name: "",
      participant_phone: "",
      video_link: "",
      video_description: "",
      privacy_policy: false,
      ai_tool: "",
      is_submit_other_contest: false,
      other_contest_name: "",
      is_agree_other_contest: true,
      is_agree_copyright: true,
    },
  });

  const { mutate: participateContest } = useMutation({
    mutationFn: async data => {
      return await axios.post(SHORT_FORM_CONTEST_PARTICIPATE_API_URL, data);
    },
    onSuccess: data => {
      if (data?.data?.code === SUCCESS_CODE) {
        setSubmitSuccess(true);
        setConfirmLoading(false);
        reset();
      }
    },
    onError: error => {
      console.log(error);
      setConfirmLoading(false);
    },
  });

  const onSubmit = useCallback(
    data => {
      // 선택된 도구들을 문자열로 변환
      const selectedTools = tools
        .filter(tool => tool.value)
        .map(tool => {
          if (tool.name === "기타" && data.ai_tool) {
            return `기타(${data.ai_tool})`;
          }
          return tool.name;
        })
        .join(", ");

      const finalData = {
        ...data,
        is_ai: isAI.yes,
        participant_phone: replaceAllDash(data?.participant_phone),
        tools: selectedTools,
      };

      participateContest(finalData);
    },
    [isAI.yes, participateContest, tools],
  );

  const showPopconfirm = async () => {
    if (isAI.yes && tools.find(tool => tool.value) === undefined) {
      setAiToolError(true);

      return;
    } else {
      setAiToolError(false);
    }

    if (
      isAI.yes &&
      tools.find(
        tool => tool.name === "기타" && tool.value && !watch("ai_tool"),
      )
    ) {
      setError("ai_tool", { message: "사용한 AI를 입력해주세요." });
      return;
    } else {
      clearErrors("ai_tool");
    }

    const output = await trigger(
      [
        "participant_name",
        "participant_email",
        "participant_phone",
        "privacy_policy",
        "other_contest_name",
      ],
      {
        shouldFocus: true,
      },
    );

    if (!output) return;

    setOpen(true);
  };

  const handleOk = () => {
    setConfirmLoading(true);
    // 폼 제출 실행
    handleSubmit(onSubmit)();
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const prevScrollTop = React.useRef(0);

  useEffect(() => {
    const modalElement = document.querySelector(".ant-modal-content");

    const handleScroll = () => {
      if (open && modalElement) {
        const currentScrollTop = modalElement.scrollTop;
        if (Math.abs(currentScrollTop - prevScrollTop.current) >= 50) {
          setOpen(false);
        }
      }
    };

    if (modalElement) {
      prevScrollTop.current = modalElement.scrollTop;
      modalElement.addEventListener("scroll", handleScroll);
      return () => {
        modalElement.removeEventListener("scroll", handleScroll);
      };
    }
  }, [open]);

  return (
    <ModalContainer
      open={modalOpen}
      onCancel={() => {
        onCancel();
        setSubmitSuccess(false);
        setOpen(false);
      }}
      footer={null}
    >
      <div>
        <Container>
          <PageTitle>97초 공모전</PageTitle>

          {submitSuccess ? (
            <Flex
              align="center"
              justify="center"
              gap="6rem"
              vertical
              style={{ paddingBlock: "3rem" }}
            >
              <SuccessImage
                src={image.declarationEstablishment}
                alt="지원이 완료되었습니다."
              />

              <CommonDescriptionTwo>
                지원이 완료되었습니다.
              </CommonDescriptionTwo>
            </Flex>
          ) : (
            <div>
              <NameField
                name="participant_name"
                control={control}
                required
                customMessage="팀으로 참여하는 경우 대표자 1인"
              />

              <EmailField name="participant_email" control={control} required />

              <PhoneField
                name="participant_phone"
                control={control}
                required
                setValue={setValue}
              />

              <TextInput
                control={control}
                name="video_link"
                placeholder={SHORT_FORM_VIDEO_LINK_PH}
                label="숏폼영상 링크"
                inputMode="url"
                customMessage={
                  <div>
                    <p>
                      유튜브, 인스타그램, 틱톡, 블로그 등 영상을 확인할 수 있는
                      SNS 링크
                    </p>

                    <p style={{ color: "red" }}>
                      ※ SNS 계정은 반드시 '전체공개' 상태여야 하며, '비공개'
                      계정은 심사 대상에서 제외됩니다.
                    </p>
                  </div>
                }
              />

              <CustomTextArea
                control={control}
                name="video_description"
                label="작품 설명"
                placeholder="작품 설명을 입력해주세요."
                customMessage="출품작에 대해 100자 이내의 간략한 설명"
              />

              <ContestCheckBoxGroup
                control={control}
                isAI={isAI}
                setIsAI={setIsAI}
                aiToolError={aiToolError}
                tools={tools}
                setTools={setTools}
                watch={watch}
              />

              <SubmitButtonWrapper justify="center">
                <Popconfirm
                  okText="확인"
                  cancelText="취소"
                  placement="top"
                  title="97초 공모전 지원하기"
                  description={
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <p>해당 지원은 수정이 불가능합니다.</p>

                      <p>이대로 지원하시겠습니까?</p>
                    </div>
                  }
                  icon={false}
                  open={open}
                  onConfirm={handleOk}
                  okButtonProps={{ loading: confirmLoading }}
                  onCancel={handleCancel}
                  zIndex={5001}
                  autoFocus={false}
                >
                  <PrimaryButton
                    bgColor={color.primary02}
                    clickEvent={showPopconfirm}
                  >
                    제출하기
                  </PrimaryButton>
                </Popconfirm>
              </SubmitButtonWrapper>
            </div>
          )}
        </Container>
      </div>
    </ModalContainer>
  );
};

export default ContestFormModal;
